/**
 * Règle à calcul — logique pure (Boîte à outils, Tle).
 *
 * Deux réglettes graduées de 1 à 10 : la distance de la graduation 1 à la
 * graduation x est proportionnelle à ln x (la règle entière mesure ln 10).
 * Faire glisser la réglette C de « ln a » puis lire en face de b donne
 * ln a + ln b = ln(ab) : multiplier revient à ajouter des longueurs.
 *
 * Positions en « décades » : 0 = graduation 1 de D, 1 = graduation 10.
 * `s` = décalage de la réglette C (le 1 de C est en s ; son 10 en s + 1),
 * `u` = position du curseur. Les valeurs posables sont celles de la grille
 * (dixièmes jusqu'à 4, puis demis) : les produits sont alors exacts.
 */

import { formatNumber } from '../../ui/format';

/** valeurs de la grille : 1 ; 1,1 ; … ; 3,9 ; 4 ; 4,5 ; … ; 10 */
export const GRID: readonly number[] = (() => {
  const out: number[] = [];
  for (let k = 10; k < 40; k++) out.push(k / 10);
  for (let k = 8; k <= 20; k++) out.push(k / 2);
  return out;
})();

export const pos = (v: number) => Math.log10(v);
const EPS = 1e-9;

/** décalages possibles : le 1 de C en face d'une valeur de la grille, ou son 10 */
export const SHIFTS: readonly number[] = (() => {
  const all = [...GRID.map((g) => pos(g) - 1), ...GRID.map((g) => pos(g))];
  return [...new Set(all.map((v) => Number(v.toFixed(12))))].sort((x, y) => x - y);
})();

const nearest = (list: readonly number[], v: number) =>
  list.reduce((best, x) => (Math.abs(x - v) < Math.abs(best - v) ? x : best), list[0]!);

export const snapShift = (s: number) => nearest(SHIFTS, s);

/** positions du curseur : les graduations de D, et celles de C là où la réglette couvre D */
export function cursorStops(s: number): number[] {
  const onC = GRID.map((g) => s + pos(g)).filter((u) => u >= -EPS && u <= 1 + EPS);
  const onD = GRID.map(pos);
  return [
    ...new Set([...onC, ...onD].map((v) => Number(Math.min(1, Math.max(0, v)).toFixed(12)))),
  ].sort((x, y) => x - y);
}

export const snapCursor = (s: number, u: number) => nearest(cursorStops(s), u);

/** pas suivant/précédent dans une liste triée */
export function stepIn(list: readonly number[], v: number, dir: 1 | -1): number {
  if (dir === 1) return list.find((x) => x > v + 1e-9) ?? list[list.length - 1]!;
  return [...list].reverse().find((x) => x < v - 1e-9) ?? list[0]!;
}

/** la valeur 10^u, ramenée à 4 décimales si elle « tombe juste » */
export function valueAt(u: number): { v: number; exact: boolean } {
  const v = 10 ** u;
  const r = Math.round(v * 1e4) / 1e4;
  return Math.abs(v - r) < 1e-9 * Math.max(1, v) ? { v: r, exact: true } : { v, exact: false };
}

/** « 2,5 » ; « ≈ 5,98 » */
export const show = (x: { v: number; exact: boolean }) =>
  x.exact ? formatNumber(x.v, 4) : `≈ ${formatNumber(x.v, 2)}`;

export const fmt = (v: number) => formatNumber(v, 4);

/** l'index de C en face de D : son 1 (s ≥ 0) ou son 10 (s < 0) */
export function indexOf(s: number): { mark: 1 | 10; on: number; exact: boolean } {
  const r = s >= -EPS ? valueAt(s) : valueAt(s + 1);
  return { mark: s >= -EPS ? 1 : 10, on: r.v, exact: r.exact };
}

/** lecture du curseur : sur C (si la réglette est là) et sur D */
export function reading(s: number, u: number) {
  const c = u - s;
  return {
    c: c >= -EPS && c <= 1 + EPS ? valueAt(c) : null,
    d: valueAt(u),
  };
}

export type Op = { kind: 'mul' | 'div'; a: number; b: number };

export interface Placement {
  s: number;
  u: number;
  /** on a dû prendre le 10 de la réglette */
  ten: boolean;
  /** lecture sur D */
  read: number;
  /** résultat de l'opération */
  result: number;
}

const round = (v: number) => Math.round(v * 1e6) / 1e6;

/** où poser la réglette et le curseur pour faire l'opération */
export function place(op: Op): Placement {
  const { a, b } = op;
  if (op.kind === 'mul') {
    const ten = a * b > 10 + EPS;
    const s = pos(a) - (ten ? 1 : 0);
    return { s, u: s + pos(b), ten, read: round(ten ? (a * b) / 10 : a * b), result: round(a * b) };
  }
  // b de C en face de a de D : le 1 de C (ou son 10) montre a/b
  const s = pos(a) - pos(b);
  const ten = s < -EPS;
  return {
    s,
    u: ten ? s + 1 : s,
    ten,
    read: round(ten ? (10 * a) / b : a / b),
    result: round(a / b),
  };
}

/** « 1,5 », « ≈ 2,67 » : un quotient, exact s'il tombe juste */
export function numText(v: number): string {
  const r = Math.round(v * 1e4) / 1e4;
  return Math.abs(v - r) < 1e-9 ? fmt(r) : `≈ ${formatNumber(v, 2)}`;
}

const ln = (v: number) => formatNumber(Math.log(v), 3);

/** l'explication de l'opération posée */
export function explain(op: Op): string {
  const p = place(op);
  const { a, b } = op;
  if (op.kind === 'mul') {
    if (!p.ten)
      return `Le 1 de la réglette C en face de ${fmt(a)} sur D, le curseur sur ${fmt(b)} de C : D lit ${fmt(p.read)}. Les longueurs s’ajoutent : ln ${fmt(a)} + ln ${fmt(b)} = ln ${fmt(p.result)} (${ln(a)} + ${ln(b)} ≈ ${ln(p.result)}), donc ${fmt(a)} × ${fmt(b)} = ${fmt(p.result)}.`;
    return `${fmt(a)} × ${fmt(b)} dépasse 10 : avec le 1 en face de ${fmt(a)}, ${fmt(b)} sortirait de la règle. On place le 10 de la réglette en face de ${fmt(a)} : D lit ${fmt(p.read)} sous ${fmt(b)}, c’est ${fmt(a)} × ${fmt(b)} ÷ 10 (on a retiré ln 10, la longueur de la règle). Donc ${fmt(a)} × ${fmt(b)} = ${fmt(p.read)} × 10 = ${fmt(p.result)}.`;
  }
  if (!p.ten)
    return `${fmt(b)} de la réglette C en face de ${fmt(a)} sur D : le 1 de la réglette montre ${numText(p.read)} sur D. Les longueurs se retranchent : ln ${fmt(a)} − ln ${fmt(b)} = ln(${fmt(a)}/${fmt(b)}), donc ${fmt(a)} ÷ ${fmt(b)} ${eqOrApprox(p.result)}.`;
  return `${fmt(b)} de la réglette en face de ${fmt(a)} : le 1 de la réglette sort à gauche, on lit sous son 10 : ${numText(p.read)}, c’est 10 × ${fmt(a)}/${fmt(b)}. Donc ${fmt(a)} ÷ ${fmt(b)} ${eqOrApprox(p.result)}.`;
}

const eqOrApprox = (v: number) => {
  const t = numText(v);
  return t.startsWith('≈') ? t : `= ${t}`;
};

/* ---------- lecture ---------- */

/** la lecture courante, en une phrase ; `calc` : écrire aussi le produit lu */
export function readout(s: number, u: number, calc = true): string {
  const idx = indexOf(s);
  const r = reading(s, u);
  const on = show({ v: idx.on, exact: idx.exact });
  const where = `Le ${idx.mark} de la réglette C est en face de ${on} sur D.`;
  if (!r.c) return `${where} Curseur : ${show(r.d)} sur D (la réglette n’est pas sous le curseur).`;
  // curseur sur l'index lui-même : D lit la position de l'index (division)
  if (r.c.exact && (r.c.v === 1 || r.c.v === 10))
    return `${where} Curseur sur le ${fmt(r.c.v)} de la réglette : ${show(r.d)} sur D.`;
  const base = `${where} Curseur : ${show(r.c)} sur C, ${show(r.d)} sur D`;
  if (!calc || !r.c.exact || !idx.exact) return `${base}.`;
  const eq = r.d.exact ? '=' : '≈';
  const d = show(r.d).replace('≈ ', '');
  if (idx.mark === 1) return `${base} : ${fmt(idx.on)} × ${fmt(r.c.v)} ${eq} ${d}.`;
  return `${base} : ${fmt(idx.on)} × ${fmt(r.c.v)} ${eq} ${d} × 10 ${eq} ${fmt(Math.round(idx.on * r.c.v * 1e4) / 1e4)}.`;
}

/* ---------- défi ---------- */

const NICE_A = [1.5, 2, 2.5, 3, 3.5, 4, 5, 6, 7, 8] as const;
const NICE_B = [1.5, 2, 2.5, 3, 4, 5, 6] as const;

/** les opérations du défi (i = 0…4) : deux produits ≤ 10, un produit > 10, deux quotients */
export function makeRound(i: number, rnd: () => number): Op {
  const pick = <T>(xs: readonly T[]) => xs[Math.floor(rnd() * xs.length)]!;
  for (let k = 0; k < 100; k++) {
    if (i <= 1) {
      const a = pick(NICE_A);
      const b = pick(NICE_B);
      if (a * b <= 10) return { kind: 'mul', a, b };
    } else if (i === 2) {
      const a = pick(NICE_A);
      const b = pick(NICE_B);
      if (a * b > 10 && a * b < 100) return { kind: 'mul', a, b };
    } else {
      // quotient exact : a = b × q, q sur la grille, a sur la grille
      const b = pick(NICE_B);
      const q = pick([1.5, 2, 2.5, 3, 4]);
      const a = b * q;
      // a exactement sur la grille (2,25 n'y est pas : on ne l'arrondit pas)
      const g = GRID.find((x) => Math.abs(x - a) < 1e-9);
      if (g !== undefined && g < 10) return { kind: 'div', a: g, b };
    }
  }
  return { kind: 'mul', a: 2, b: 3 };
}

export type Verdict = 'ok' | 'index' | 'ten' | 'cursor';

/** la réglette et le curseur sont-ils bien posés ? */
export function check(op: Op, s: number, u: number): Verdict {
  const near = (x: number, y: number) => Math.abs(x - y) < 1e-6;
  const configs: Array<{ s: number; u: number }> = [];
  if (op.kind === 'mul') {
    for (const [x, y] of [
      [op.a, op.b],
      [op.b, op.a],
    ] as const)
      for (const ten of [false, true]) {
        const cs = pos(x) - (ten ? 1 : 0);
        const cu = cs + pos(y);
        if (cu >= -1e-9 && cu <= 1 + 1e-9) configs.push({ s: cs, u: cu });
      }
  } else {
    const cs = pos(op.a) - pos(op.b);
    for (const cu of [cs, cs + 1])
      if (cu >= -1e-9 && cu <= 1 + 1e-9) configs.push({ s: cs, u: cu });
  }
  if (configs.some((c) => near(c.s, s) && near(c.u, u))) return 'ok';
  if (configs.some((c) => near(c.s, s))) return 'cursor';
  // le 1 en face de a alors que le produit dépasse 10 : il fallait le 10
  if (op.kind === 'mul' && op.a * op.b > 10 + 1e-9 && (near(s, pos(op.a)) || near(s, pos(op.b))))
    return 'ten';
  return 'index';
}

export function verdictText(op: Op, v: Verdict): string {
  const { a, b } = op;
  if (op.kind === 'mul')
    switch (v) {
      case 'ten':
        return `Avec le 1 en face de ${fmt(a)}, ${fmt(b)} tombe hors de la règle : le produit dépasse 10. Place le 10 de la réglette en face de ${fmt(a)}.`;
      case 'index':
        return a * b > 10 + 1e-9
          ? `Le produit dépasse 10 : place le 10 de la réglette C en face de ${fmt(a)} sur D.`
          : `Place d’abord le 1 de la réglette C en face de ${fmt(a)} sur D.`;
      case 'cursor':
        return `La réglette est bien placée. Mets maintenant le curseur sur ${fmt(b)}, sur la réglette C.`;
      default:
        return explain(op);
    }
  switch (v) {
    case 'index':
    case 'ten':
      return `Place ${fmt(b)} de la réglette C en face de ${fmt(a)} sur D.`;
    case 'cursor':
      return 'La réglette est bien placée. Mets le curseur sur le 1 de la réglette (ou sur son 10 s’il sort) : D y lit le quotient.';
    default:
      return explain(op);
  }
}

export const opText = (op: Op) => `${fmt(op.a)} ${op.kind === 'mul' ? '×' : '÷'} ${fmt(op.b)}`;
